// Category + Price checklist filters. Filters the product cards already on the page (by the
// data-cats / data-price attributes on each .product-grid__item) and keeps the item count in
// the page heading in step. Re-applies itself whenever the grid is re-rendered (e.g. by sorting).
const PRICE_STEP = 500;

// Shared across re-renders: sorting re-renders the filter bar from the server, which resets
// the checkboxes and empties the price list, so the choices live outside the element.
const selected = { category: new Set(), price: new Set() };
// Price range (min/max in rupees). null = no limit. Defaults show the shop's lowest/highest price,
// but the slider and number boxes go below / above that.
const priceRange = { min: null, max: null };

class ShopFilters extends HTMLElement {
  #selected = selected;
  #busy = false;

  connectedCallback() {
    this.clearButton = this.querySelector('.shop-filters__clear');

    // Stop the store's own filter form from reacting to these checkboxes.
    for (const type of ['change', 'input']) {
      this.addEventListener(type, (event) => {
        event.stopPropagation();
        const box = event.target;
        if (!(box instanceof HTMLInputElement) || box.type !== 'checkbox') return;
        const set = this.#selected[box.dataset.group];
        if (box.checked) set.add(box.value);
        else set.delete(box.value);
        this.apply();
      });
    }

    this.addEventListener('input', (event) => this.#onRangeInput(event), true);

    this.clearButton.addEventListener('click', () => {
      this.#selected.category.clear();
      this.#selected.price.clear();
      priceRange.min = priceRange.max = null;
      this.#syncRangeUI();
      this.querySelectorAll('input[type="checkbox"]').forEach((box) => (box.checked = false));
      this.apply();
    });

    // One dropdown open at a time; close on outside click or Escape.
    this.querySelectorAll('details').forEach((details) =>
      details.addEventListener('toggle', () => {
        if (!details.open) return;
        this.querySelectorAll('details').forEach((other) => other !== details && (other.open = false));
        this.#closeSortMenu();
      })
    );
    document.addEventListener('click', (event) => {
      if (!this.contains(event.target)) this.querySelectorAll('details').forEach((d) => (d.open = false));
    });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') this.querySelectorAll('details').forEach((d) => (d.open = false));
    });

    // The grid is replaced on sort / pagination: filter the new cards too.
    const list = document.querySelector('results-list') ?? document.body;
    new MutationObserver(() => {
      if (!this.#busy) this.apply();
    }).observe(list, { childList: true, subtree: true });

    this.apply();
  }

  #closeSortMenu() {
    document.querySelectorAll('.sorting-filter details[open]').forEach((d) => (d.open = false));
  }

  get #items() {
    return [...document.querySelectorAll('.product-grid__item')];
  }

  get #bounds() {
    const prices = this.#items.map((item) => Number(item.dataset.price) / 100).filter(Boolean);
    return prices.length ? [Math.floor(Math.min(...prices)), Math.ceil(Math.max(...prices))] : [0, 0];
  }

  #buildPriceBands() {
    const panel = this.querySelector('[data-panel="price"]');
    if (!panel || panel.children.length) return;
    const [lo, hi] = this.#bounds;
    if (!hi) return;
    const currency = panel.dataset.currency ?? '';
    // slider spans beyond the shop's prices so you can go lower / higher
    const sMin = Math.max(0, Math.floor((lo * 0.5) / 100) * 100);
    const sMax = Math.ceil((hi * 1.5) / 100) * 100;
    panel.innerHTML = `
      <div class="shop-filters__range" data-lo="${lo}" data-hi="${hi}" data-smin="${sMin}" data-smax="${sMax}">
        <div class="shop-filters__range-track">
          <i class="shop-filters__range-fill"></i>
          <i class="shop-filters__range-mark" style="left: ${((lo - sMin) / (sMax - sMin)) * 100}%" data-label="${currency}${lo.toLocaleString('en-IN')}" title="Lowest price in shop"></i>
          <i class="shop-filters__range-mark" style="left: ${((hi - sMin) / (sMax - sMin)) * 100}%" data-label="${currency}${hi.toLocaleString('en-IN')}" title="Highest price in shop"></i>
          <input type="range" min="${sMin}" max="${sMax}" step="50" value="${lo}" data-range="min" aria-label="Minimum price">
          <input type="range" min="${sMin}" max="${sMax}" step="50" value="${hi}" data-range="max" aria-label="Maximum price">
        </div>
        <div class="shop-filters__range-boxes">
          <label>Min<span>${currency}<input type="number" inputmode="numeric" min="0" step="50" value="${lo}" data-box="min"></span></label>
          <span class="shop-filters__range-dash">–</span>
          <label>Max<span>${currency}<input type="number" inputmode="numeric" min="0" step="50" value="${hi}" data-box="max"></span></label>
        </div>
      </div>`;
    this.#syncRangeUI();
  }

  #onRangeInput(event) {
    const el = event.target;
    if (!(el instanceof HTMLInputElement) || !(el.dataset.range || el.dataset.box)) return;
    event.stopPropagation();
    const which = el.dataset.range || el.dataset.box;
    priceRange[which] = el.value === '' ? null : Number(el.value);
    if (priceRange.min != null && priceRange.max != null && priceRange.min > priceRange.max) {
      if (which === 'min') priceRange.max = priceRange.min;
      else priceRange.min = priceRange.max;
    }
    this.#syncRangeUI(el);
    this.apply();
  }

  #syncRangeUI(except) {
    const box = this.querySelector('.shop-filters__range');
    if (!box) return;
    const lo = Number(box.dataset.lo);
    const hi = Number(box.dataset.hi);
    const sMin = Number(box.dataset.smin);
    const sMax = Number(box.dataset.smax);
    const pct = (v) => Math.min(100, Math.max(0, ((v - sMin) / (sMax - sMin)) * 100));
    const fill = box.querySelector('.shop-filters__range-fill');
    if (fill) {
      fill.style.left = pct(priceRange.min ?? lo) + '%';
      fill.style.right = 100 - pct(priceRange.max ?? hi) + '%';
    }
    for (const which of ['min', 'max']) {
      const val = priceRange[which] ?? (which === 'min' ? lo : hi);
      for (const input of box.querySelectorAll(`[data-range="${which}"], [data-box="${which}"]`)) {
        if (input !== except) input.value = val;
      }
    }
  }


  // "Nothing matches" message under the (empty) grid, with a way back.
  #toggleEmptyMessage(empty) {
    const grid = document.querySelector('.product-grid');
    if (!grid) return;
    let note = document.querySelector('.shop-filters__empty');
    if (!empty) return note?.remove();
    if (!note) {
      note = document.createElement('div');
      note.className = 'shop-filters__empty';
      note.innerHTML =
        '<p class="shop-filters__empty-title">No products match these filters</p>' +
        '<p>Try removing a filter or choosing a different category or price.</p>' +
        '<button type="button" class="shop-filters__empty-clear">Clear all filters</button>';
      note.querySelector('button').addEventListener('click', () => this.clearButton.click());
    }
    if (note.previousElementSibling !== grid) grid.after(note);
  }

  apply() {
    this.#busy = true;
    this.#buildPriceBands();
    this.querySelectorAll('input[type="checkbox"]').forEach((box) => {
      box.checked = this.#selected[box.dataset.group]?.has(box.value) ?? false;
    });
    const { category, price } = this.#selected;
    const ranges = [...price].map((band) => band.split('-').map(Number));
    const pMin = priceRange.min;
    const pMax = priceRange.max;
    let shown = 0;

    for (const item of this.#items) {
      const cats = (item.dataset.cats ?? '').split(' ').filter(Boolean);
      const cost = Number(item.dataset.price) / 100;
      const okCategory = !category.size || cats.some((cat) => category.has(cat));
      const okPrice =
        (!ranges.length || ranges.some(([lo, hi]) => cost >= lo && cost < hi)) &&
        (pMin == null || cost >= pMin) &&
        (pMax == null || cost <= pMax);
      item.hidden = !(okCategory && okPrice);
      if (!item.hidden) shown += 1;
    }

    for (const [group, set] of Object.entries(this.#selected)) {
      const label = this.querySelector(`[data-group="${group}"] [data-label]`);
      if (label) label.textContent = set.size ? `${label.dataset.label} (${set.size})` : label.dataset.label;
    }
    const [bLo, bHi] = this.#bounds;
    const rangeOn = (pMin != null && pMin !== bLo) || (pMax != null && pMax !== bHi);
    const priceLabel = this.querySelector('[data-group="price"] [data-label]');
    if (priceLabel && rangeOn) priceLabel.textContent = `${priceLabel.dataset.label} (1)`;
    this.clearButton.hidden = !(category.size || price.size || rangeOn);

    const count = document.querySelector('.collection-count p');
    if (count) {
      count.dataset.total ??= count.textContent.trim();
      count.textContent = category.size || price.size ? String(shown) : count.dataset.total;
    }
    this.#toggleEmptyMessage(shown === 0 && (category.size || price.size));
    queueMicrotask(() => (this.#busy = false));
  }
}

if (!customElements.get('shop-filters')) customElements.define('shop-filters', ShopFilters);
